import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { ShortText } from "./ShortText/ShortText";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import styles from './Form.module.scss';
import { ElementCreator } from "./ElementCreator/ElementCreator";
import { LongText } from "./LongText/LongText";
import { Paragraph } from "./Paragraph/Paragraph";
import { MultiSelection } from "./MultiSelection/MultiSelection";
import { SingleSelection } from "./SingleSelection/SingleSelection";
import BtButton from "../../../ts-components/bt-button/bt-button";
import Editor from "../Editor/Editor";
import {ReactComponent as EditIcon} from "../../../assets/svg/editIcon.svg";
import {ReactComponent as OpenedEyeIcon} from "../../../assets/svg/openedEyeIcon.svg";
import { useTranslation } from "react-i18next";

export enum Type {
  ShortTextField = 'ShortTextField',
  LongTextField = 'LongTextField',
  MultiSelectionElement = 'MultiSelectionElement',
  SingleSelectionElement = 'SingleSelectionElement',
  ParagraphElement = 'ParagraphElement',
}

export type Element = ShortText | LongText | Paragraph | MultiSelection | SingleSelection;

const FORM_STATUS = {
  UNANSWERED: 'UNANSWERED',
  ANSWERED: 'ANSWERED',
} as const;
type FORM_STATUS = typeof FORM_STATUS[keyof typeof FORM_STATUS];

export const Mode = {
  FORM: 'FORM',
  READ_ONLY: 'READ_ONLY',
  EDIT: 'EDIT',
} as const;
export type Mode = typeof Mode[keyof typeof Mode];
export type ModeWithoutReadOnly = Omit<Mode, 'READ_ONLY'>;

export type Form = {
  status: FORM_STATUS,
  elements: Element[],
  mode: Mode,
}

const getActualMode = (status: FORM_STATUS, mode: Mode) => {
  return status === FORM_STATUS.ANSWERED ? Mode.READ_ONLY : mode;
}

export const readFormFromDB = (dbForm: any, mode: Mode) => {
  const form = {
    status: dbForm.status ?? 'UNANSWERED',
    elements: (!dbForm.elements && mode === Mode.EDIT) ? [new ShortText()] : (dbForm.elements ?? []).map((element: any) => {
      const { type, ...rest } = element;
      switch (type) {
        case Type.ShortTextField:
          return new ShortText(rest);
        case Type.LongTextField:
          return new LongText(rest);
        case Type.ParagraphElement:
          return new Paragraph(rest);
        case Type.MultiSelectionElement:
          return new MultiSelection(rest);
        case Type.SingleSelectionElement:
          return new SingleSelection(rest);
        default:
          return new ShortText(rest);
      }
    }),
    mode,
  }

  return form as Form;
}

export const getSubmittableForm = (form: Form, onFormChange: (form: Form) => void, mode: ModeWithoutReadOnly): Form | null => {
  let isValid = true;
  form.elements.forEach((element) => {
    const isValidElement = element.validateField(mode as Mode);
    if (!isValidElement) {
      isValid = false;
    }
  })

  onFormChange({ ...form }); // trigger re-render to show any validation errors
  if (isValid) {
    const data = {
      status: form.status,
      elements: form.elements.map((element) => element.getForDB(mode)),
    }
    return data as Form;
  }
  return null;
}

export const getErrorMessage = (t: (key: string) => string, errorMessageKey?: string) => {
  return errorMessageKey ? t(`errorMessage.${errorMessageKey}`) : '';
}

type QuestionFieldProps = {
  errorMessage?: string,
  value: string,
  onChange: (value: string) => void,
  label?: string,
}

export const QuestionField: FC<QuestionFieldProps> = ({ errorMessage, value, onChange, label }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'form' });
  return <Editor hideToolbar errorMessage={errorMessage} hideStatusbar wrapperStyle={{ height: '64px' }} value={value} placeholder={label ?? t('questionPlaceholder', 'Question *')} onChange={onChange} />
};

type Props = {
  form: Form,
  onFormChange: Dispatch<SetStateAction<Form | undefined>>,
  onSubmit: (form: Form) => Promise<void>,
  formModeLabel?: string,
  getRenderWrapperForMode?: (mode: Mode) => (children: JSX.Element) => JSX.Element,
  goLiveButtonOptions?: {
    label: string,
    url: string,
  },
}

export const Form: FC<Props> = ({ form, onFormChange, onSubmit, getRenderWrapperForMode, goLiveButtonOptions }) => {
  const [localMode, setLocalMode] = useState(getActualMode(form.status, form.mode));
  const [shouldScroll, setShouldScroll] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [t] = useTranslation('shared', { keyPrefix: 'form' });

  const onUpdate = async (data: Element[]) => {
    onFormChange({ ...form, elements: data });
  }

  const handleOnDragEnd = async (result: DropResult) => {
    const items = Array.from(form.elements);
    if (result.destination) {
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
    }

    onUpdate(items);
  }

  useEffect(() => {
    if (shouldScroll) {
      requestAnimationFrame(() => {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        setShouldScroll(false);
      });
    }
  }, [form.elements, shouldScroll]);

  const renderListOfElements = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} className={styles.elementList}>
        {form.elements.map((element) => {
          return element.render(localMode, (elem) => onFormChange({ ...form, elements: form.elements.map(e => e.renderId === elem.renderId ? elem : e) }))
        })}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {getActualMode(form.status, form.mode) === Mode.EDIT && (
        <div style={{ display: 'flex', gap: '16px' }}>
          <BtButton onClick={() => setLocalMode(Mode.EDIT)} size="xxs" color="white" disabled={localMode === Mode.EDIT} leftIcon={<EditIcon style={{ fill: 'var(--gray-700)', width: '16px', height: '16px' }} />}>{t('modeSwitcher.edit', 'Edit')}</BtButton>
          <BtButton onClick={() => setLocalMode(Mode.FORM)} size="xxs" color="white" disabled={localMode === Mode.FORM} leftIcon={<OpenedEyeIcon style={{ fill: 'var(--gray-700)', width: '16px', height: '16px' }} />}>{t('modeSwitcher.preview', 'Preview')}</BtButton>
          {goLiveButtonOptions && <BtButton size="xxs" color="white" onClick={() => window.open(goLiveButtonOptions.url)}>{goLiveButtonOptions.label}</BtButton>}
        </div>
      )}
      {localMode === Mode.EDIT && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="columns">
              {(provided) => (
                <ul {...provided.droppableProps} className={styles.elementCreatorList} style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }} ref={provided.innerRef}>
                  {form.elements.map((element, index) => {
                    return (
                      <ElementCreator
                        key={element.renderId}
                        element={element}
                        onElementChange={(elem: Element | null) => onFormChange({ ...form, elements: elem ? form.elements.map(e => e.renderId === elem.renderId ? elem : e) : form.elements.filter(e => e.renderId !== element.renderId) })}
                        index={index}
                      />
                    )
                  })}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
      {(localMode === Mode.FORM || localMode === Mode.READ_ONLY) && (
        getRenderWrapperForMode ? getRenderWrapperForMode(localMode)(renderListOfElements()) : renderListOfElements()
      )}

      {getActualMode(form.status, form.mode) === Mode.EDIT && ( // lookup the original mode
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {localMode === Mode.EDIT ? (
              <BtButton style={{ width: 'fit-content' }} loaderShow={buttonLoading} disabled={submitting} color='white' onClick={() => {
                setButtonLoading(true);
                setTimeout(() => {
                  onFormChange({...form, elements: [...form.elements, new ShortText()]});
                  setShouldScroll(true);
                  setButtonLoading(false);
                }, 150);
              }}>{t(`addElement`, 'Add element')}</BtButton>
            ) : <div></div>}
            
            <BtButton style={{ width: 'fit-content' }} loaderShow={submitting} onClick={async () => {
              setSubmitting(true);
              
              const submittableForm = getSubmittableForm(form, onFormChange, Mode.EDIT);
              if (submittableForm) {
                await onSubmit(submittableForm);
              }

              setSubmitting(false);
            }}>{t(`edit.save`, 'Save')}</BtButton>
          </div>
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  )
}